import { useEffect, useState } from 'react';
import { createClient } from '@blinkdotnew/sdk';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Brain, Mail, Users, TrendingUp, Sparkles, RefreshCw, Database } from 'lucide-react';
import { userModelService, UserModel, EmailStatistics, Contact } from '@/services/userModelService';
import { demoDataService } from '@/services/demoDataService';
import { toast } from '@/hooks/use-toast';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
});

export default function UserProfile() {
  const [user, setUser] = useState<any>(null);
  const [userModel, setUserModel] = useState<UserModel | null>(null);
  const [statistics, setStatistics] = useState<EmailStatistics | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);

  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user && !state.isLoading) {
        setUser(state.user);
        await loadUserData(state.user.id);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const loadUserData = async (userId: string) => {
    setLoading(true);
    try {
      const [model, stats, topContacts] = await Promise.all([
        userModelService.getUserModel(userId),
        userModelService.getEmailStatistics(userId),
        userModelService.getTopContacts(userId, 10),
      ]);

      setUserModel(model);
      setStatistics(stats);
      setContacts(topContacts);
      
      // Check if this is demo data
      const hasData = model || stats || topContacts.length > 0;
      setIsDemoMode(hasData && model?.totalEmailsAnalyzed === 1000);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDemoData = async () => {
    if (!user) return;
    
    setSeedingDemo(true);
    try {
      const result = await demoDataService.seedDemoData(user.id);
      if (result.success) {
        toast({
          title: 'Demo data created',
          description: `Successfully generated ${result.stats?.emails || 0} demo emails. Reloading page...`,
        });
        
        // Reload data and page to show demo content
        await loadUserData(user.id);
        
        // Force page refresh to ensure all components load demo data
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error seeding demo data:', error);
      toast({
        title: 'Error',
        description: 'Failed to seed demo data',
        variant: 'destructive',
      });
    } finally {
      setSeedingDemo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (!userModel) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-6 w-6 text-yellow-500" />
              No User Model Found
            </CardTitle>
            <CardDescription>
              Create your AI profile by generating demo data or connecting your email accounts.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Database className="h-4 w-4" />
                Quick Start: Generate Demo Data
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Create 1,000 realistic demo emails, contacts, and AI insights to explore the platform immediately.
              </p>
              <Button 
                onClick={handleSeedDemoData} 
                disabled={seedingDemo}
                className="w-full"
              >
                {seedingDemo ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Generating Demo Data...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Demo Data
                  </>
                )}
              </Button>
            </div>
            
            <div className="text-center text-sm text-muted-foreground">
              <p>Or connect your email account to analyze your real communications</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header with DEMO tag */}
      <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="h-8 w-8 text-yellow-500 animate-pulse" />
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold">Your AI Profile</h1>
                  <Badge variant="secondary" className="bg-primary/20 text-primary-foreground">
                    DEMO
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Deep intelligence analysis based on {userModel.totalEmailsAnalyzed} communications
                </p>
              </div>
            </div>
            <Button onClick={() => user && loadUserData(user.id)} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Confidence Score</p>
                <p className="text-3xl font-bold">{userModel.confidenceScore}%</p>
              </div>
              <Sparkles className="h-8 w-8 text-yellow-500" />
            </div>
            <Progress value={userModel.confidenceScore} className="mt-2" />
          </CardContent>
        </Card>

        <Card className="glass-card border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Emails Analyzed</p>
                <p className="text-3xl font-bold">{userModel.totalEmailsAnalyzed}</p>
              </div>
              <Mail className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Contacts</p>
                <p className="text-3xl font-bold">{contacts.length}</p>
              </div>
              <Users className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Model Status</p>
                <Badge variant={userModel.modelStatus === 'completed' ? 'default' : 'secondary'} className="text-xs">
                  {userModel.modelStatus}
                </Badge>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analysis */}
      <Tabs defaultValue="behavioral" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="behavioral">Behavioral Profile</TabsTrigger>
          <TabsTrigger value="communication">Communication</TabsTrigger>
          <TabsTrigger value="contacts">Top Contacts</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
        </TabsList>

        <TabsContent value="behavioral" className="space-y-4">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Behavioral Summary</CardTitle>
              <CardDescription>AI-generated insights about your work style and personality</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="prose prose-sm max-w-none">
                <p className="text-muted-foreground leading-relaxed">{userModel.behavioralSummary || 'No behavioral summary available yet.'}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <p className="text-sm font-medium mb-2">Personality Traits</p>
                  <div className="flex flex-wrap gap-2">
                    {(userModelService.parseModelField(userModel.personalityTraits) || []).map((trait: string) => (
                      <Badge key={trait} variant="secondary">{trait}</Badge>
                    ))}
                    {(!userModel.personalityTraits || userModelService.parseModelField(userModel.personalityTraits)?.length === 0) && (
                      <span className="text-xs text-muted-foreground">No traits identified yet</span>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Work Style</p>
                  <Badge variant="outline">{userModel.workStyle || 'Not analyzed'}</Badge>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Decision Making</p>
                  <Badge variant="outline">{userModel.decisionMakingStyle || 'Not analyzed'}</Badge>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Priorities</p>
                  <div className="flex flex-wrap gap-2">
                    {(userModelService.parseModelField(userModel.priorities) || []).map((priority: string) => (
                      <Badge key={priority} variant="secondary">{priority}</Badge>
                    ))}
                    {(!userModel.priorities || userModelService.parseModelField(userModel.priorities)?.length === 0) && (
                      <span className="text-xs text-muted-foreground">No priorities defined yet</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="communication" className="space-y-4">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Communication Preferences</CardTitle>
              <CardDescription>How you prefer to communicate professionally</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(userModelService.parseModelField(userModel.communicationPreferences) || []).map((pref: string) => (
                  <div key={pref} className="flex items-center gap-2">
                    <Badge variant="default">{pref}</Badge>
                  </div>
                ))}
                {(!userModel.communicationPreferences || userModelService.parseModelField(userModel.communicationPreferences)?.length === 0) && (
                  <p className="text-sm text-muted-foreground">No communication preferences analyzed yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {statistics && (
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Email Activity</CardTitle>
                <CardDescription>Your recent email patterns</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Received</p>
                    <p className="text-2xl font-bold">{statistics.totalReceived || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Sent</p>
                    <p className="text-2xl font-bold">{statistics.totalSent || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Urgent</p>
                    <p className="text-2xl font-bold">{statistics.totalUrgent || 0}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">Busiest Hours</p>
                  <div className="flex flex-wrap gap-2">
                    {(userModelService.parseModelField(statistics.busiestHours) || []).map((hour: string) => (
                      <Badge key={hour} variant="outline">{hour}</Badge>
                    ))}
                    {(!statistics.busiestHours || userModelService.parseModelField(statistics.busiestHours)?.length === 0) && (
                      <span className="text-xs text-muted-foreground">No peak hours identified</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4">
          {contacts.length > 0 ? (
            contacts.map((contact) => (
              <Card key={contact.id} className="glass-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{contact.name || contact.email}</CardTitle>
                      <CardDescription>{contact.email}</CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{contact.aiRelationshipScore || 0}</p>
                      <p className="text-xs text-muted-foreground">Relationship Score</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Type</p>
                      <Badge variant="outline">{contact.relationshipType || 'Unknown'}</Badge>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Stage</p>
                      <Badge variant="outline">{contact.relationshipStage || 'Unknown'}</Badge>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Engagement</p>
                      <Badge variant="outline">{contact.engagementLevel || 'Unknown'}</Badge>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Interactions</p>
                      <p className="font-medium">{contact.totalInteractions || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="glass-card">
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">No contacts analyzed yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="statistics" className="space-y-4">
          {statistics ? (
            <>
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Communication Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Average Response Time</span>
                      <span className="font-bold">{statistics.avgResponseTimeHours || 0} hours</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Response Rate</span>
                      <span className="font-bold">
                        {userModelService.parseModelField(statistics.communicationTrends)?.responseRate 
                          ? `${(userModelService.parseModelField(statistics.communicationTrends).responseRate * 100).toFixed(0)}%` 
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Weekly Average</span>
                      <span className="font-bold">
                        {userModelService.parseModelField(statistics.communicationTrends)?.weeklyAverage || 'N/A'} emails
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Top Contacts by Volume</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(userModelService.parseModelField(statistics.topContacts) || []).length > 0 ? (
                      userModelService.parseModelField(statistics.topContacts)?.map((email: string, index: number) => (
                        <div key={email} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                          <span className="text-sm">{email}</span>
                          <Badge variant="secondary">#{index + 1}</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No contact data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="glass-card">
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">No statistics available yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
